<i18n
  src="@/locales/views/organization/production/productivity_by_harvest_team/show_process.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/harvest_team_work_log.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_processes')"
    :params="{ organization_id: organization_id }"
  />

  <bread-crumbs :labels="[module_name, harvest_process.name]" />

  <template v-if="load_data_general">
    <h1 class="page-title">
      <i class="fa-solid fa-crate-apple"></i>
      <span class="space-icon"></span>
      {{ $t("labels.title") }}
    </h1>

    <!-- Botón para actualizar datos -->
    <div class="mt-6 flex flex-row justify-center">
      <div class="space-x-3">
        <success-button
          v-if="!charging_submit"
          @click.prevent="syncData()"
          :label="$t('labels.sync_button')"
        />
        <success-button-processing
          v-else
          :label="$t('labels.sync_button')"
        />
      </div>
    </div>

    <!-- Botones de descarga por año y mes -->
    <div class="flex flex-wrap justify-center gap-2 mt-4" v-if="months.length > 0">
      <button type="button" @click.prevent="downloadYear()" class="btn-white">
        <i v-if="downloading_report['process']" class="fa-solid fa-sync fa-spin"></i>
        <i v-else class="fa-solid fa-file-excel"></i>
        <span class="space-icon"></span> {{ $t("labels.download_year") }}
      </button>
      <button
        v-for="month in months"
        :key="month.value"
        type="button"
        @click.prevent="downloadMonth(month)"
        class="btn-white"
      >
        <i v-if="downloading_report[month.value]" class="fa-solid fa-sync fa-spin"></i>
        <i v-else class="fa-solid fa-file-excel"></i>
      <span class="space-icon"></span> {{ $t("labels.download_month", { month: month.label } ) }}
      </button>
    </div>

    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.work_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.worker_count") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.worked_hours") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.productivity_ratio") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.harvest_team") }}
                  </th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="harvest_team_work_log in harvest_team_work_logs"
                  :key="harvest_team_work_log.id"
                >
                  <td class="table-cell">
                    {{ dateFormatter(harvest_team_work_log.work_date) }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.worker_count }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.worked_hours }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.productivity_ratio }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.harvest_team.harvest_team_code }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <table-paginator
      v-model="current_page"
      :total_rows="total_harvest_team_work_logs"
      :rows_per_page="rows_per_page"
    />
  </template>

  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/production/productivity_by_harvest_team/show_process.js";
import TablePaginator from "@/components/views/layout/TablePaginator.vue";

export default {
  name: "OrgProcessWorkLogsShowProcess",
  components: { TablePaginator },
  data() {
    return {
      load_data_general: false,
      current_page: 1,
      total_harvest_team_work_logs: 0,
      rows_per_page: 20,
      harvest_process: {},
      harvest_team_work_logs: [],
      charging_submit: false,
      months: [],
      downloading_report: {}
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true },
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchHarvestTeamWorkLogsTotal();
      await this.fetchHarvestTeamWorkLogs();
      await this.fetchAvailableMonths();
      this.load_data_general = true;
    },

    fetchHarvestProcess: async function () {
      let response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },

    fetchHarvestTeamWorkLogsTotal: async function () {
      let response = await Requester.fetchHarvestTeamWorkLogsTotal(
        this.organization_id,
        this.harvest_process_id
      );
      this.total_harvest_team_work_logs = response.data;
    },

    fetchHarvestTeamWorkLogs: async function () {
      let response = await Requester.fetchHarvestTeamWorkLogs(
        this.organization_id,
        this.harvest_process_id,
        this.current_page - 1,
        this.rows_per_page
      );
      this.harvest_team_work_logs = response.data;
    },

    fetchAvailableMonths: async function () {
      let response = await Requester.fetchAvailableMonths(
        this.organization_id,
        this.harvest_process_id
      );
      this.months = response.data;
    },

    syncData: async function () {
      this.charging_submit = true;
      await Requester.syncData(this.organization_id, this.harvest_process_id);
      await this.fetchAvailableMonths();
      this.charging_submit = false;
    },

    downloadYear: async function () {
      this.downloading_report['process'] = true;
      try {
        var response = await Requester.downloadRepByProcessXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        this.downloadFile(file, `productividad.xlsx`);
      } catch (error) {
        error;
      }
      this.downloading_report['process'] = false;
    },

    downloadMonth: async function (month) {
      this.downloading_report[month.value] = true;
      try {
        var response = await Requester.downloadRepByMonthXlsx(
          this.organization_id,
          this.harvest_process_id,
          month.year,
          month.month
        );
        const file = response.data;
        this.downloadFile(file, `productividad_${month.year}_${month.month}.xlsx`);
      } catch (error) {
        error;
      }
      this.downloading_report[month.value] = false;
    },
  },
  watch: {
    current_page: function () {
      this.fetchHarvestTeamWorkLogs();
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
};
</script>
